/**
 * The sentence the signed-out calendar pages show, built from live counts.
 *
 * IMPORTS NOTHING, ON PURPOSE. scripts/check-calendar.js evaluates this file in
 * isolation to assert the wording and the plurals, the same way it does the
 * browser copy of the county display helper. A single import here would make
 * that impossible to run, so the React component that renders the line lives in
 * its own file and this one stays pure text.
 *
 * The static tagline it replaces is kept as the fallback rather than deleted.
 * These numbers arrive one request after the page does, and a signup page whose
 * first line is blank for half a second, or blank forever when the API is
 * unreachable, is worse than one that says something true and unchanging.
 */

export const CALENDAR_FALLBACK_TAGLINE =
  'Tax deed and tax lien sale dates, county by county, kept current by hand.';

/** "1 state" or "4 states". */
function countOf(value, singular, plural) {
  const number = Number(value) || 0;
  return `${number.toLocaleString('en-US')} ${number === 1 ? singular : plural}`;
}

/**
 * "3 states live, 412 counties tracked, 47 states coming soon".
 *
 * "tracked" rather than "with sale dates" because the count behind it is every
 * county with a sale row, dated or not. Most counties have a typical month
 * rather than a confirmed date at any given moment, and those are researched,
 * paid for and shown in the product exactly like the dated ones.
 *
 * Returns '' when there is nothing to say, which is what the caller shows the
 * fallback for. An all-zero answer means the counts have not loaded or the
 * calendar has no data yet, and "0 states live" on a signup page is a reason
 * not to subscribe.
 */
export function formatCalendarStats(stats) {
  if (!stats) return '';

  const live = Number(stats.statesLive) || 0;
  const counties = Number(stats.countiesTracked) || 0;
  const soon = Number(stats.statesComingSoon) || 0;

  if (live === 0 && counties === 0 && soon === 0) return '';

  return [
    `${countOf(live, 'state', 'states')} live`,
    `${countOf(counties, 'county', 'counties')} tracked`,
    `${countOf(soon, 'state', 'states')} coming soon`
  ].join(', ');
}
